import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  listActiveReseaux,
  fetchUserReseauIds,
  joinReseau,
  leaveReseau,
  type Reseau,
} from '@/lib/networks';

// ─────────────────────────────────────────────────────────────
// Carte « Réseaux ».
// Deux rôles :
//  • commerce    : le commerce demande à apparaître sur les
//                  marketplaces partenaires du réseau.
//  • marketplace : la marketplace rejoint le réseau pour accueillir
//                  les commerces membres.
// Les réseaux listés sont ceux créés par le superadmin (page Réseaux),
// filtrés par cible (« chez qui cela doit s'afficher »).
// ─────────────────────────────────────────────────────────────

interface ZifekNetworkCardProps {
  role: 'commerce' | 'marketplace';
  /** Callback après changement d'état. */
  onChanged?: (on: boolean) => void;
}

export default function ZifekNetworkCard({ role, onChanged }: ZifekNetworkCardProps) {
  const { user, updateUser } = useAuth();
  const [reseaux, setReseaux] = useState<Reseau[]>([]);
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [openRules, setOpenRules] = useState<number | null>(null);

  const userId = user ? Number(user.id) : 0;

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const [list, ids] = await Promise.all([
        listActiveReseaux(role),
        fetchUserReseauIds(userId),
      ]);
      setReseaux(list);
      setMemberIds(ids);
    } finally {
      setLoading(false);
    }
  }, [userId, role]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (reseau: Reseau) => {
    if (!userId) return;
    const member = memberIds.includes(reseau.id);
    setBusy(reseau.id);
    setError('');
    try {
      if (member) {
        await leaveReseau(userId, reseau);
        setMemberIds((prev) => prev.filter((id) => id !== reseau.id));
        if (/zifek/i.test(reseau.titre)) updateUser({ reseau_zifek: 0 });
      } else {
        await joinReseau(userId, reseau);
        setMemberIds((prev) => [...prev, reseau.id]);
        if (/zifek/i.test(reseau.titre)) updateUser({ reseau_zifek: 1 });
      }
      onChanged?.(!member);
    } catch {
      setError('Impossible de mettre à jour votre appartenance au réseau. Réessayez.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-foreground-400 py-4">
        <i className="ri-loader-4-line animate-spin"></i>Chargement des réseaux…
      </div>
    );
  }

  if (reseaux.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-background-200/70 bg-background-50 p-6 text-center">
        <div className="w-12 h-12 mx-auto rounded-xl bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-share-forward-line text-xl text-foreground-400"></i>
        </div>
        <p className="text-sm text-foreground-500">
          {role === 'commerce'
            ? 'Aucun réseau n’est ouvert aux professionnels pour le moment.'
            : 'Aucun réseau n’est ouvert aux marketplaces pour le moment.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1.5">
          <i className="ri-error-warning-line"></i>
          {error}
        </p>
      )}

      {reseaux.map((reseau) => {
        const on = memberIds.includes(reseau.id);
        const saving = busy === reseau.id;
        const showRules = openRules === reseau.id;
        return (
          <div
            key={reseau.id}
            className={`rounded-xl border p-5 md:p-6 ${on ? 'border-accent-200 bg-accent-50/50' : 'border-background-200/70 bg-background-50'}`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${on ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                <i className={`${reseau.icon || 'ri-share-forward-line'} text-xl`}></i>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold font-heading text-foreground-950">{reseau.titre}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${on ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                    {on ? 'Membre du réseau' : 'Non membre'}
                  </span>
                </div>

                {reseau.description && (
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed max-w-2xl">{reseau.description}</p>
                )}

                {reseau.regles && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setOpenRules(showRules ? null : reseau.id)}
                      className="flex items-center gap-1.5 text-xs font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
                    >
                      <i className="ri-file-list-3-line"></i>
                      Règles du réseau
                      <i className={`${showRules ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`}></i>
                    </button>
                    {showRules && (
                      <pre className="mt-2 whitespace-pre-wrap break-words rounded-lg bg-background-100 px-3 py-2.5 text-[11px] leading-relaxed text-foreground-600 font-sans">
                        {reseau.regles}
                      </pre>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => toggle(reseau)}
                  disabled={saving || !userId}
                  className={`mt-4 flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                    on
                      ? 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  <i className={saving ? 'ri-loader-4-line animate-spin' : on ? 'ri-logout-box-r-line' : 'ri-add-circle-line'}></i>
                  {saving ? 'Traitement...' : on ? 'Quitter le réseau' : 'Demander à rejoindre le réseau'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}